import { Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router'
import { SALE_STATUSES } from '@shared/contracts/enums'
import type { SaleStatus, CurrencySchema } from '@shared/contracts/enums'
import { DATE_PRESET_OPTIONS } from '@shared/lib/date-range'
import type { DatePreset } from '@shared/lib/date-range'
import { AuthGate, SessionOutlet } from './AuthGate'
import { DashboardPage } from '@pages/DashboardPage'
import { VendasPage } from '@pages/VendasPage'
import { ProdutosPage } from '@pages/ProdutosPage'
import { AreaMembrosPage } from '@pages/AreaMembrosPage'
import { AssinaturasPage } from '@pages/AssinaturasPage'
import { AfiliadosPage } from '@pages/AfiliadosPage'
import { CoproducoesPage } from '@pages/CoproducoesPage'
import { FinancasPage } from '@pages/FinancasPage'
import { EmailNotificationsPage } from '@pages/EmailNotificationsPage'
import { ConfigPage } from '@pages/ConfigPage'
import { WebhooksPage } from '@pages/WebhooksPage'
import { LoginPage } from '@pages/LoginPage'
import { CadastroPage } from '@pages/CadastroPage'
import { EsqueciSenhaPage } from '@pages/EsqueciSenhaPage'
import { RedefinirSenhaPage } from '@pages/RedefinirSenhaPage'
import { VerificarEmailPage } from '@pages/VerificarEmailPage'
import { OnboardingPage } from '@pages/OnboardingPage'
import { IndicacoesPage } from '@pages/IndicacoesPage'
import {
  MemberAccountRoutePage,
  MemberCourseRoutePage,
  MemberFilesRoutePage,
  MemberLessonRoutePage,
  MemberLibraryRoutePage,
  MemberLockedCourseRoutePage,
  MemberLoginRoutePage,
  MemberMagicRoutePage,
} from '@pages/MemberAccessPages'
import {
  MemberPreviewCourseRoutePage,
  MemberPreviewLessonRoutePage,
  MemberPreviewOfferRoutePage,
  MemberPreviewRoutePage,
} from '@pages/MemberPreviewPages'
import { RecuperaAiPage } from '@pages/RecuperaAiPage'
import type { RecuperaAiSearch, RecuperaPeriod } from '@features/recupera-ai'

type SalesView = 'all' | 'new' | 'renewals'
type DashboardRange = '7d' | '30d' | 'month' | 'custom'

const SALES_VIEWS: readonly SalesView[] = ['all', 'new', 'renewals']
const DASHBOARD_RANGES: readonly DashboardRange[] = ['7d', '30d', 'month', 'custom']

type DashboardSearch = {
  range?: DashboardRange
  from?: string
  to?: string
  currency?: (typeof CurrencySchema.options)[number]
  view?: SalesView
  productId?: string
  live?: boolean
}

type VendasSearch = {
  scope?: 'charge'
  sale?: string
  q?: string
  status?: SaleStatus[]
  view?: SalesView
  currency?: (typeof CurrencySchema.options)[number]
  preset?: DatePreset
  from?: string
  to?: string
}

type AssinaturasSearch = {
  preset?: DatePreset
  from?: string
  to?: string
  sub?: string
  currency?: (typeof CurrencySchema.options)[number]
}

type FinancasSearch = {
  direction?: 'IN' | 'OUT'
  currency?: (typeof CurrencySchema.options)[number]
  q?: string
  entry?: string
  preset?: DatePreset
  from?: string
  to?: string
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function asFlag(value: unknown): true | undefined {
  return value === true || value === 1 || value === '1' || value === 'true' ? true : undefined
}

function asEnumArray<T extends string>(value: unknown, allowed: readonly T[]): T[] | undefined {
  const raw = Array.isArray(value) ? value : value === undefined || value === null ? [] : [value]
  const filtered = raw.filter(
    (item): item is T => typeof item === 'string' && (allowed as readonly string[]).includes(item),
  )
  return filtered.length > 0 ? filtered : undefined
}

function asPreset(value: unknown): DatePreset | undefined {
  return typeof value === 'string' && (DATE_PRESET_OPTIONS as string[]).includes(value)
    ? (value as DatePreset)
    : undefined
}

function asView(value: unknown): SalesView | undefined {
  return typeof value === 'string' && (SALES_VIEWS as readonly string[]).includes(value)
    ? (value as SalesView)
    : undefined
}

function asDashboardRange(value: unknown): DashboardRange | undefined {
  return typeof value === 'string' && (DASHBOARD_RANGES as readonly string[]).includes(value)
    ? (value as DashboardRange)
    : undefined
}

function asCurrency(_value: unknown): 'BRL' {
  return 'BRL'
}

function prune<T extends Record<string, unknown>>(value: T): Partial<T> {
  const result: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value)) {
    if (item !== undefined) {
      result[key] = item
    }
  }
  return result as Partial<T>
}

const rootRoute = createRootRoute({ component: Outlet })

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: AuthGate,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const cadastroRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/cadastro',
  component: CadastroPage,
  validateSearch: (search: Record<string, unknown>) => ({
    ref:
      typeof search.ref === 'string' && /^[A-Za-z0-9_-]{32}$/.test(search.ref)
        ? search.ref
        : undefined,
  }),
})

const esqueciSenhaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/esqueci-senha',
  component: EsqueciSenhaPage,
})

const redefinirSenhaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/redefinir-senha/$token',
  component: () => {
    const params = redefinirSenhaRoute.useParams()
    return <RedefinirSenhaPage token={params.token} />
  },
})

const redefinirSenhaQueryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/redefinir-senha',
  component: RedefinirSenhaPage,
})

const verificarEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verificar-email/$token',
  component: () => {
    const params = verificarEmailRoute.useParams()
    return <VerificarEmailPage token={params.token} />
  },
})

const verificarEmailQueryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verificar-email',
  component: VerificarEmailPage,
})

const memberLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId',
  component: MemberLoginRoutePage,
})

const memberMagicRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId/entrar',
  validateSearch: (search: Record<string, unknown>) => ({
    token: asString(search.token),
  }),
  component: MemberMagicRoutePage,
})

const memberLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId/biblioteca',
  component: MemberLibraryRoutePage,
})

const memberAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId/conta',
  component: MemberAccountRoutePage,
})

const memberCourseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId/cursos/$productId',
  component: MemberCourseRoutePage,
})

const memberLockedCourseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId/cursos/$productId/oferta',
  component: MemberLockedCourseRoutePage,
})

const memberLessonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId/cursos/$productId/aulas/$lessonId',
  component: MemberLessonRoutePage,
})

const memberFilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/acesso/$producerId/arquivos/$productId',
  component: MemberFilesRoutePage,
})

const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>): DashboardSearch =>
    prune<DashboardSearch>({
      range: asDashboardRange(search.range),
      from: asString(search.from),
      to: asString(search.to),
      currency: asCurrency(search.currency),
      view: asView(search.view),
      productId: asString(search.productId),
      live: asFlag(search.live),
    }),
  component: DashboardPage,
})

const vendasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/vendas',
  validateSearch: (search: Record<string, unknown>): VendasSearch =>
    prune<VendasSearch>({
      sale: asString(search.sale),
      scope: search.scope === 'charge' ? 'charge' : undefined,
      q: asString(search.q),
      status: asEnumArray(search.status, SALE_STATUSES),
      view: asView(search.view),
      currency: asCurrency(search.currency),
      preset: asPreset(search.preset),
      from: asString(search.from),
      to: asString(search.to),
    }),
  component: VendasPage,
})

const produtosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/produtos',
  component: ProdutosPage,
})

const areaMembrosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/area-membros',
  component: AreaMembrosPage,
})

const sellerPreviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'seller-preview',
  component: SessionOutlet,
})

const memberPreviewRoute = createRoute({
  getParentRoute: () => sellerPreviewRoute,
  path: '/area-membros/preview/$productId',
  component: MemberPreviewRoutePage,
})

const memberPreviewCourseRoute = createRoute({
  getParentRoute: () => sellerPreviewRoute,
  path: '/area-membros/preview/$productId/curso',
  component: MemberPreviewCourseRoutePage,
})

const memberPreviewOfferRoute = createRoute({
  getParentRoute: () => sellerPreviewRoute,
  path: '/area-membros/preview/$productId/oferta',
  component: MemberPreviewOfferRoutePage,
})

const memberPreviewLessonRoute = createRoute({
  getParentRoute: () => sellerPreviewRoute,
  path: '/area-membros/preview/$productId/aulas/$lessonId',
  component: MemberPreviewLessonRoutePage,
})

const assinaturasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/assinaturas',
  validateSearch: (search: Record<string, unknown>): AssinaturasSearch =>
    prune<AssinaturasSearch>({
      preset: asPreset(search.preset),
      from: asString(search.from),
      to: asString(search.to),
      sub: asString(search.sub),
      currency: asCurrency(search.currency),
    }),
  component: AssinaturasPage,
})

const afiliadosRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/afiliados',
  component: AfiliadosPage,
})

const affiliateInviteRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/affiliates/invite/$token',
  component: () => {
    const params = affiliateInviteRoute.useParams()
    return <AfiliadosPage inviteToken={params.token} />
  },
})

const coproducoesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/coproducoes',
  component: CoproducoesPage,
})

const coproductionInviteRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/coproducao/aceitar/$token',
  component: () => {
    const params = coproductionInviteRoute.useParams()
    return <CoproducoesPage inviteToken={params.token} />
  },
})

const financasRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/financas',
  validateSearch: (search: Record<string, unknown>): FinancasSearch =>
    prune<FinancasSearch>({
      direction:
        search.direction === 'IN' || search.direction === 'OUT' ? search.direction : undefined,
      currency: asCurrency(search.currency),
      q: asString(search.q),
      entry: asString(search.entry),
      preset: asPreset(search.preset),
      from: asString(search.from),
      to: asString(search.to),
    }),
  component: FinancasPage,
})

const onboardingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/onboarding',
  component: OnboardingPage,
})

const configRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config',
  component: ConfigPage,
})

const configNotificationsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config/notificacoes',
  component: EmailNotificationsPage,
})

const configWebhooksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/config/webhooks',
  component: WebhooksPage,
})

const indicacoesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/indicacoes',
  component: IndicacoesPage,
})

const RECUPERA_PERIODS: RecuperaPeriod[] = ['24h', '7d', '30d', 'month', 'all']
const RECUPERA_STATUSES = ['abandoned', 'recovered', 'external'] as const
const RECUPERA_SORTS = ['date_desc', 'date_asc', 'name_asc', 'price_asc', 'price_desc'] as const

const recuperaAiRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/recupera-ai',
  validateSearch: (search: Record<string, unknown>): RecuperaAiSearch =>
    prune<RecuperaAiSearch>({
      status: RECUPERA_STATUSES.includes(search.status as (typeof RECUPERA_STATUSES)[number])
        ? (search.status as RecuperaAiSearch['status'])
        : undefined,
      sort: RECUPERA_SORTS.includes(search.sort as (typeof RECUPERA_SORTS)[number])
        ? (search.sort as RecuperaAiSearch['sort'])
        : undefined,
      q: typeof search.q === 'string' && search.q.trim() !== '' ? search.q : undefined,
      product:
        typeof search.product === 'string' && search.product.trim() !== ''
          ? search.product
          : undefined,
      period: RECUPERA_PERIODS.includes(search.period as RecuperaPeriod)
        ? (search.period as RecuperaPeriod)
        : undefined,
    }),
  component: RecuperaAiPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  cadastroRoute,
  esqueciSenhaRoute,
  redefinirSenhaRoute,
  redefinirSenhaQueryRoute,
  verificarEmailRoute,
  verificarEmailQueryRoute,
  memberLoginRoute,
  memberMagicRoute,
  memberLibraryRoute,
  memberAccountRoute,
  memberCourseRoute,
  memberLockedCourseRoute,
  memberLessonRoute,
  memberFilesRoute,
  sellerPreviewRoute.addChildren([
    memberPreviewRoute,
    memberPreviewCourseRoute,
    memberPreviewOfferRoute,
    memberPreviewLessonRoute,
  ]),
  protectedRoute.addChildren([
    indicacoesRoute,
    indexRoute,
    vendasRoute,
    produtosRoute,
    areaMembrosRoute,
    assinaturasRoute,
    afiliadosRoute,
    affiliateInviteRoute,
    coproducoesRoute,
    coproductionInviteRoute,
    financasRoute,
    onboardingRoute,
    recuperaAiRoute,
    configRoute,
    configWebhooksRoute,
    configNotificationsRoute,
  ]),
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
